'use strict';

const EASE_MORPH = 'cubic-bezier(0.32, 0.72, 0, 1)';
const EASE_SETTLE = 'cubic-bezier(0.33, 0, 0.2, 1)';
const SPRING_DAMPING_IN = 0.68;
const SPRING_DAMPING_OUT = 0.9;
const SPRING_DECAY = 7;
const SPRING_SAMPLES = 40;
const SETTLE_DECAY = 9.2;
const OVERSHOOT_MAX_PX = 34;
const SQUASH_MAX = 0.024;
const SQUASH_FULL_TRAVEL_PX = 520;
const OPEN_BASE_MS = 280;
const OPEN_MS_PER_PX = 0.34;
const OPEN_MAX_MS = 500;
const CLOSE_BASE_MS = 200;
const CLOSE_MS_PER_PX = 0.14;
const CLOSE_MAX_MS = 300;
const CONTENT_RISE_PX = 6;

const CONTENT_IN_DELAY_RATIO = 0.1;
const CONTENT_FADE_RATIO = 0.5;

export const FOCUS_AT_RATIO = 0.7;
export const MAIN_BAR_RETURN_MS = 220;
export const DEFAULT_SURFACE_RADIUS = 24;

export function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function visualRadius(element, height) {
    const raw = parseFloat(getComputedStyle(element).borderRadius) || DEFAULT_SURFACE_RADIUS;
    return Math.min(raw, height / 2);
}

function morphDuration(travel, opening) {
    const distance = Math.abs(travel);
    return opening
        ? Math.min(OPEN_MAX_MS, Math.round(OPEN_BASE_MS + distance * OPEN_MS_PER_PX))
        : Math.min(CLOSE_MAX_MS, Math.round(CLOSE_BASE_MS + distance * CLOSE_MS_PER_PX));
}

function springStep(damping) {
    const wobble = Math.sqrt(1 - damping * damping);
    const angular = (SPRING_DECAY / damping) * wobble;
    return (tau) => 1 - Math.exp(-SPRING_DECAY * tau)
        * (Math.cos(angular * tau) + (damping / wobble) * Math.sin(angular * tau));
}

function peakOvershoot(damping) {
    const wobble = Math.sqrt(1 - damping * damping);
    return Math.exp(-Math.PI * damping / wobble);
}

function overshootScale(travel, damping, headroom) {
    if (travel <= 0) return 0;
    const wanted = travel * peakOvershoot(damping);
    if (wanted < 1) return 1;
    return Math.max(0, Math.min(1, Math.min(headroom, OVERSHOOT_MAX_PX) / wanted));
}

function springProgressSamples(damping, excessScale) {
    const step = springStep(damping);
    const rise = Array.from({ length: SPRING_SAMPLES + 1 }, (unused, index) => step(index / SPRING_SAMPLES));
    const peakIndex = rise.reduce((best, value, index) => (value > rise[best] ? index : best), 0);
    const peak = rise[peakIndex];
    const tailSpan = SPRING_SAMPLES - peakIndex;

    return rise.map((value, index) => {
        const settling = tailSpan > 0 && index > peakIndex;
        const decayed = settling ? ((index - peakIndex) / tailSpan) * SETTLE_DECAY : 0;
        const raw = settling ? 1 + (peak - 1) * (1 + decayed) * Math.exp(-decayed) : value;
        const progress = index === SPRING_SAMPLES ? 1 : raw;
        return progress > 1 ? 1 + (progress - 1) * excessScale : progress;
    });
}

function normalizedVelocities(progress) {
    const raw = progress.map((unused, index) => {
        if (index === 0 || index === progress.length - 1) return 0;
        return progress[index + 1] - progress[index - 1];
    });
    const peak = Math.max(...raw.map(Math.abs)) || 1;
    return raw.map((value) => value / peak);
}

function squashAmplitude(travel) {
    return Math.min(1, Math.abs(travel) / SQUASH_FULL_TRAVEL_PX) * SQUASH_MAX;
}

function springKeyframes(fromHeight, toHeight, fromRadius, toRadius, headroom) {
    const travel = toHeight - fromHeight;
    const damping = travel > 0 ? SPRING_DAMPING_IN : SPRING_DAMPING_OUT;
    const progress = springProgressSamples(damping, overshootScale(travel, damping, headroom));
    const velocities = normalizedVelocities(progress);
    const squash = squashAmplitude(travel);

    return progress.map((value, index) => ({
        offset: index / SPRING_SAMPLES,
        height: `${fromHeight + travel * value}px`,
        borderRadius: `${fromRadius + (toRadius - fromRadius) * Math.min(1, Math.max(0, value))}px`,
        transform: `scaleX(${1 - squash * velocities[index]})`,
    }));
}

export function animateHeightSpring(element, geometry) {
    const { fromHeight, toHeight, fromRadius, toRadius, headroom = Infinity } = geometry;
    const duration = morphDuration(toHeight - fromHeight, toHeight > fromHeight);
    const animation = element.animate(
        springKeyframes(fromHeight, toHeight, fromRadius, toRadius, headroom),
        { duration, easing: 'linear', fill: 'forwards' }
    );
    return { animation, duration };
}

export function revealContent(element, duration) {
    return element.animate(
        [
            { opacity: 0, transform: `translateY(${CONTENT_RISE_PX}px)` },
            { opacity: 1, transform: 'none' },
        ],
        {
            duration: Math.round(duration * CONTENT_FADE_RATIO),
            delay: Math.round(duration * CONTENT_IN_DELAY_RATIO),
            easing: EASE_MORPH,
            fill: 'both',
        }
    );
}

export function dismissContent(element, duration) {
    return element.animate(
        [
            { opacity: 1, transform: 'none' },
            { opacity: 0, transform: `translateY(${CONTENT_RISE_PX}px)` },
        ],
        { duration: Math.round(duration * CONTENT_FADE_RATIO), easing: EASE_SETTLE, fill: 'forwards' }
    );
}

export function fadeOut(element, duration) {
    return element.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: Math.round(duration * CONTENT_FADE_RATIO), easing: EASE_SETTLE, fill: 'forwards' }
    );
}

export function fadeIn(element, duration) {
    return element.animate([{ opacity: 0 }, { opacity: 1 }], { duration, easing: EASE_SETTLE });
}
